import type { ReactElement } from "react"
import React from "react"
import _t from "@core/i18n"
import CalculatorUsersIcon from "@onestore-icons/calculator-users.svg"
import HomeIcon from "@onestore-icons/home-icon.svg"
import { format } from "date-fns/format"
import { get } from "lodash"
import ButtonAtm from "@onestore/hel/dist/components/atoms/ButtonAtm"
import TextAtm from "@onestore/hel/dist/components/atoms/TextAtm"
import MarketplaceCalculatorOrg from "@onestore/hel/dist/components/organisms/MarketplaceCalculatorOrg"
import { BasketActionSource } from "@gatsby-plugin-basket/store/actions"
import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import Media from "@gatsby-plugin-generic-page/components/Media"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import type { CostCalculator } from "~/fragments/costCalculator"
import type { SaleConfiguration } from "~/fragments/saleConfigurations"
import { setAddCartSource } from "~/lib/basket"
import isEmpty from "~/lib/isEmpty"
import ConfigurationBox from "./components/ConfigurationBox"
import useCostCalculator from "./useCostCalculator"

export type ProductCostCalculatorProps = {
  saleConfiguration: SaleConfiguration
  defaultCloudBluePlanData: CloudBluePlan["flatData"]
  calculatorData: CostCalculator
  addCartSource?: BasketActionSource
}

const defaultMaxUsersNumber = 150

export default function ProductCostCalculator({
  saleConfiguration,
  defaultCloudBluePlanData,
  calculatorData,
  addCartSource = BasketActionSource.GENERIC_PAGE,
}: ProductCostCalculatorProps): ReactElement<ProductCostCalculatorProps> | null {
  const {
    activePlanDataId,
    defaultConnectedControlsTitle,
    defaultParametersTitle,
    usersNumber,
    externalSupplierPrice,
    connectedProductSelectOnChange,
    getConnectedControlsSelect,
    getParametersSelect,
    getActiveParameterName,
    parametersSelectOnChange,
    getProductName,
    getCalculatorPrices,
    inputRangeOnChange,
    inputNumberOnChange,
    getButtonUrl,
  } = useCostCalculator(saleConfiguration, defaultCloudBluePlanData)
  const { currentPriceType } = usePriceTypeContext()

  const defaultPlanExternalSupplierPrice = get(
    saleConfiguration,
    "flatData.defaultCloudBluePlan[0].flatData.externalSupplierPrice"
  )

  if (isEmpty(defaultPlanExternalSupplierPrice)) {
    return null
  }

  const {
    connectedControlsCalculatorTitle,
    optionsCalculatorTitle,
    optionsCalculatorIcon,
    maxUsersNumber,
    calculatorUpdateDate,
  } = calculatorData

  const { externalPrice, internalPrice, savingsPrice, periodText } =
    getCalculatorPrices()

  const updatedOptionsCalculatorTitle =
    optionsCalculatorTitle || defaultParametersTitle

  const parametersParam = !isEmpty(updatedOptionsCalculatorTitle)
    ? [
        {
          key: "parametersParam",
          icon: <Media media={optionsCalculatorIcon} />,
          title: (
            <TextAtm typography="small2">
              {updatedOptionsCalculatorTitle}
            </TextAtm>
          ),
          value: (
            <TextAtm typography="small3">{getActiveParameterName()}</TextAtm>
          ),
        },
      ]
    : []

  const usersCountParam = {
    key: "usersCountParam",
    icon: <CalculatorUsersIcon />,
    title: (
      <TextAtm typography="small2">{_t("marketplace.usersCount")}:</TextAtm>
    ),
    value: <TextAtm typography="small3">{usersNumber}</TextAtm>,
  }

  const priceBox = {
    title: (
      <TextAtm typography="small2" pushSpace={2}>
        {_t("marketplace.priceComparison")}
      </TextAtm>
    ),
    externalPriceItem: {
      key: "externalPriceItem",
      icon:
        !isEmpty(externalSupplierPrice) &&
        !isEmpty(externalSupplierPrice.icon) ? (
          <Media media={externalSupplierPrice.icon} />
        ) : undefined,
      title: (
        <TextAtm typography="medium2_s">{externalSupplierPrice?.text}</TextAtm>
      ),
      value: (
        <TextAtm typography="medium3_s">
          {externalPrice}{" "}
          <TextAtm typography="nano1">
            {_t(`prices.${currentPriceType}`)} {periodText}
          </TextAtm>
        </TextAtm>
      ),
    },
    internalPriceItem: {
      key: "internalPriceItem",
      icon: <HomeIcon />,
      title: (
        <TextAtm typography="medium2_s">{_t("marketplace.homePrice")}</TextAtm>
      ),
      value: (
        <TextAtm typography="medium3_s" color="success">
          {internalPrice}{" "}
          <TextAtm typography="nano1">
            {_t(`prices.${currentPriceType}`)} {periodText}
          </TextAtm>
        </TextAtm>
      ),
    },
    summaryTitle: (
      <TextAtm typography="medium2_s">{_t("marketplace.save")}:</TextAtm>
    ),
    summaryPrice: (
      <TextAtm typography="medium3_s" color="success">
        {savingsPrice}
      </TextAtm>
    ),
  }

  return (
    <>
      <MarketplaceCalculatorOrg
        configurationBox={
          <ConfigurationBox
            connectedControlsCalculatorTitle={
              connectedControlsCalculatorTitle ||
              defaultConnectedControlsTitle ||
              ""
            }
            connectedControlsSelect={getConnectedControlsSelect()}
            optionsCalculatorTitle={updatedOptionsCalculatorTitle || ""}
            parametersSelect={getParametersSelect()}
            maxUsersNumber={maxUsersNumber || defaultMaxUsersNumber}
            usersNumber={usersNumber}
            connectedProductSelectOnChange={connectedProductSelectOnChange}
            parametersSelectOnChange={parametersSelectOnChange}
            inputRangeOnChange={inputRangeOnChange}
            inputNumberOnChange={inputNumberOnChange}
          />
        }
        resultBox={{
          title: (
            <TextAtm typography="big3" pushSpace={2}>
              {getProductName()}
            </TextAtm>
          ),
          params: [...parametersParam, usersCountParam],
          priceBox: priceBox,
          additionalInfo: !isEmpty(calculatorUpdateDate) ? (
            <TextAtm typography="nano1">
              {`${_t("marketplace.comparisonDate")} ${format(new Date(calculatorUpdateDate), "dd.MM.yyyy")}`}
            </TextAtm>
          ) : null,
          button: (
            <ButtonAtm
              onClick={() => setAddCartSource(activePlanDataId, addCartSource)}
              href={getButtonUrl()}
              title={_t("marketplace.addToCard")}
            >
              {_t("marketplace.addToCard")}
            </ButtonAtm>
          ),
        }}
      />
    </>
  )
}
